import React, { useId, useMemo } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import type { ProductReadinessOption } from 'containers/Intelligence/registry/productReadiness';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import InsightSelect from '../InsightSelect';

export interface IProductReadinessSelectProps {
  /** The tenant industry's catalogue, in the order it should be offered. */
  options: ProductReadinessOption[];
  /** The selected option's `id`; absent draws the placeholder. */
  value?: string;
  onChange: (optionId: string) => void;
  disabled?: boolean;
  className?: string;
}

const Host = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

const Label = styled.label`
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  text-transform: uppercase;
`;

const SELECT_WIDTH = '260px';

const ProductReadinessSelect: React.FC<IProductReadinessSelectProps> = ({
  options,
  value,
  onChange,
  disabled,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const selectId = useId();

  const selectOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : []).map(option => ({
        value: option.id,
        label: option.label
      })),
    [options]
  );

  // An industry with no catalogue entry has nothing to choose between; the card draws its own
  // "not configured" state rather than an empty control.
  if (selectOptions.length === 0) {
    return null;
  }

  return (
    <Host className={className} data-testid="product-readiness-select">
      <Label htmlFor={selectId}>{t(`${TCustomerInsights}.ProductReadiness.SelectLabel`)}</Label>
      <InsightSelect
        id={selectId}
        options={selectOptions}
        value={value}
        placeholder={t(`${TCustomerInsights}.ProductReadiness.SelectPlaceholder`)}
        disabled={disabled}
        width={SELECT_WIDTH}
        onChange={onChange}
      />
    </Host>
  );
};

export default ProductReadinessSelect;
